import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { SidebarProvider } from './context/SidebarContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EmployeeList from './pages/EmployeeList';
import EmployeeFormComprehensive from './pages/EmployeeFormComprehensive';
import EmployeeProfile from './pages/EmployeeProfile';
import ColorPalette from './pages/ColorPalette';
import Departments from './pages/Departments';
import DepartmentMaster from './pages/DepartmentMaster';
import DesignationMaster from './pages/DesignationMaster';
import LeaveTypesMaster from './pages/LeaveTypesMaster';
import PasswordChange from './pages/PasswordChange';

// RequireAuth component to handle protected routes with redirect
function RequireAuth({ user, children }) {
  const location = useLocation();
  
  if (!user) {
    // Redirect to login but save the attempted URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
}
import LocationMaster from './pages/LocationMaster';
import SystemSettings from './pages/SystemSettings';
import AttendanceWithCamera from './pages/AttendanceWithCamera';
import Leaves from './pages/Leaves';
import LeaveConfiguration from './pages/LeaveConfiguration';
import LeaveBalanceAllocation from './pages/LeaveBalanceAllocation';
import AttendanceRegularization from './pages/AttendanceRegularization';
import ShiftManagement from './pages/ShiftManagement';
import HolidayManagement from './pages/HolidayManagement';
import OffsiteAttendance from './pages/OffsiteAttendance';
import OffsiteAttendanceV2 from './pages/OffsiteAttendanceV2';
import BulkOperationsPage from './pages/BulkOperationsPage';
import FileManagementPage from './pages/FileManagementPage';
import NotificationsPage from './pages/NotificationsPage';
import ClaimsManagement from './pages/ClaimsManagement';
import ReportsAnalytics from './pages/ReportsAnalytics';
import OnboardingWorkflowPage from './pages/OnboardingWorkflowPage';
import Payroll from './pages/Payroll';
import PayrollConfiguration from './pages/PayrollConfiguration';
import PayslipManagement from './pages/PayslipManagement';
import ComplianceReports from './pages/ComplianceReports';
import UnifiedPerformanceManagement from './pages/UnifiedPerformanceManagement';
import ApprovalDashboard from './pages/ApprovalDashboard';
import OnboardingDashboard from './pages/OnboardingDashboard';
import EmployeeSelfService from './pages/EmployeeSelfService';
import RecruitmentDashboard from './pages/RecruitmentDashboard';
import EnhancedHRDashboard from './pages/EnhancedHRDashboard';
import EmailInbox from './pages/EmailInbox';
import DocumentDrive from './pages/DocumentDrive';
import EmailManagement from './pages/EmailManagement';
import KanbanBoard from './pages/KanbanBoard';
import WhatsAppSettings from './pages/WhatsAppSettings';
import WhatsAppWidget from './pages/WhatsAppWidget';
import SellPhone from './pages/SellPhoneCashify';
import BuybackLeads from './pages/BuybackLeads';
import BuybackWhatsAppTemplates from './pages/BuybackWhatsAppTemplates';
import PQCConfiguration from './pages/QCConfigurationNew';
import BuybackDashboard from './pages/BuybackDashboard';
import BuybackPriceComparison from './pages/BuybackPriceComparison';

// Pickup Engineer PWA
import EngineerApp from './pages/EngineerApp';
import PickupDetail from './pages/PickupDetail';

// Shop (B2C Public Website) Pages
import ShopHomepage from './pages/shop/ShopHomepage';
import ShopAbout from './pages/shop/ShopAbout';
import ShopContact from './pages/shop/ShopContact';
import ShopPolicy from './pages/shop/ShopPolicy';
import ShopStores from './pages/shop/ShopStores';
import WhatsAppInbox from './pages/WhatsAppInbox';
import WhatsAppContacts from './pages/WhatsAppContacts';
import WhatsAppCampaigns from './pages/WhatsAppCampaigns';
import WhatsAppAnalytics from './pages/WhatsAppAnalytics';
import WhatsAppAnalyticsAdvanced from './pages/WhatsAppAnalyticsAdvanced';
import WhatsAppAutomation from './pages/WhatsAppAutomation';
import WhatsAppCannedResponses from './pages/WhatsAppCannedResponses';
import WhatsAppChatbotSettings from './pages/WhatsAppChatbotSettings';
import Documents from './pages/Documents';
import Announcements from './pages/Announcements';
import Onboarding from './pages/Onboarding';
import Shifts from './pages/Shifts';
import Holidays from './pages/Holidays';
import CompanyPolicies from './pages/CompanyPolicies';
import Assets from './pages/Assets';
import AllAssets from './pages/AllAssets';
import AssetDashboard from './pages/AssetDashboard';
import AssetAllocation from './pages/AssetAllocation';
import AssetReturns from './pages/AssetReturns';
import AssetHistory from './pages/AssetHistory';
import HRPolicies from './pages/HRPolicies';
import TravelExpense from './pages/TravelExpense';
import Legal from './pages/Legal';
import RoleManagement from './pages/RoleManagement';
import Reports from './pages/Reports';
import PricingDiscussion from './pages/PricingDiscussion';
import ProductManagement from './pages/ProductManagement';
import ProductDetail from './pages/ProductDetail';
import HRMSDashboard from './pages/HRMSDashboard';
import FranchiseeManagementUnified from './pages/FranchiseeManagementUnified';
import StoreOperations from './pages/StoreOperations';
import StoreSetup from './pages/StoreSetup';
import VendorRegistration from './pages/VendorRegistration';
import SupplierManagement from './pages/SupplierManagement';
import PurchaseManagement from './pages/PurchaseManagement';
import ProductPurchase from './pages/ProductPurchase';
import RequisitionManagement from './pages/RequisitionManagement';
import RequisitionApproval from './pages/RequisitionApproval';
import SupplierPortal from './pages/SupplierPortal';
import FinanceManagement from './pages/FinanceManagement';
import IQCForm from './pages/IQCForm';
import TRCManagement from './pages/TRCManagement';
import ParameterGradeMapping from './pages/ParameterGradeMapping';
import ApproverReview from './pages/ApproverReview';
import ApproverReviewNew from './pages/ApproverReviewNew';
import ApproverTestScreen from './pages/ApproverTestScreen';
import BinMaster from './pages/BinMaster';
import WarehouseSTN from './pages/WarehouseSTN';
import TRCSRN from './pages/TRCSRN';
import CQCConfiguration from './pages/CQCConfiguration';
import SellPhoneWidget from './pages/SellPhoneWidget';
import IQCQueue from './pages/IQCQueue';
import IQCConfiguration from './pages/IQCConfiguration';
import EngineerDashboard from './pages/EngineerDashboard';
import TRCInventory from './pages/TRCInventory';
import TRCAnalytics from './pages/TRCAnalytics';
import DeviceDetails from './pages/DeviceDetails';
import NotificationsCenter from './pages/NotificationsCenter';
import PartsInventory from './pages/PartsInventory';
import TRCDevicesBulk from './pages/TRCDevicesBulk';
import StatusDashboard from './pages/StatusDashboard';
import ASCPerformance from './pages/ASCPerformance';
import QCDashboard from './pages/QCDashboard';
import IQCEngine from './pages/IQCEngine';
import FQCEngine from './pages/FQCEngine';
import TRCBinning from './pages/TRCBinning';
import EngineerDevices from './pages/EngineerDevices';
import EngineerPerformanceDashboard from './pages/EngineerPerformanceDashboard';
import EngineerLeaderboard from './pages/EngineerLeaderboard';
import EngineerLiveTracking from './pages/EngineerLiveTracking';
import RepairManagement from './pages/RepairManagement';
import FQCPage from './pages/FQCPage';
import QCControlModule from './pages/QCControlModule';
import CQCFlow from './pages/CQCFlow';
import TRCAdmin from './pages/TRCAdmin';
import PartsConsumption from './pages/PartsConsumption';
import ASCManagement from './pages/ASCManagement';
import ASCDispatch from './pages/ASCDispatch';
import ASCDispatches from './pages/ASCDispatches';
// B2C Module
import B2CCMS from './pages/B2CCMS';
// New TRC Management Pages
import TRCInventoryDashboard from './pages/TRCInventoryDashboard';
import CreateSTN from './pages/CreateSTN';
import CreateSRN from './pages/CreateSRN';
import StoreInventory from './pages/StoreInventory';
import RFRManagement from './pages/RFRManagement';
import ProductivityDashboard from './pages/ProductivityDashboard';
import SpareDashboard from './pages/SpareDashboard';
import SpareMaster from './pages/SpareMaster';
import BOMManagement from './pages/BOMManagement';
import SpareJourney from './pages/SpareJourney';
import PartRequest from './pages/PartRequest';
import RepairDashboard from './pages/RepairDashboard';
import GradingEngineConfig from './pages/GradingEngineConfig';
import ASCDashboard from './pages/ASCDashboard';
import CreateASCDispatch from './pages/CreateASCDispatch';
import WarehouseDashboard from './pages/WarehouseDashboard';
import BuybackManifests from './pages/BuybackManifests';
import GRNProcessing from './pages/GRNProcessing';
import DIDLabelPrinting from './pages/DIDLabelPrinting';
import LabelConfig from './pages/LabelConfig';
import CreateGRN from './pages/CreateGRN';
import WarehouseReports from './pages/WarehouseReports';
import OQCForm from './pages/OQCForm';
import QCMasterConfig from './pages/QCMasterConfig';
import PQCGradingConfig from './pages/PQCGradingConfig';
import CRM from './pages/CRM';
import CustomerDashboard from './pages/CustomerDashboard';
import Customer360 from './pages/Customer360';
import MyAccount from './pages/MyAccount';
import CallCenter from './pages/CallCenter';
import ExpressIVRCallCenter from './pages/ExpressIVRCallCenter';
import IDCardManagement from './pages/IDCardManagement';
import BulkUploadPage from './pages/BulkUploadPage';
import ProfileManagement from './pages/ProfileManagement';
import AppointmentLetterFlow from './pages/AppointmentLetterFlow';
import EnhancedEmployeeOnboarding from './pages/EnhancedEmployeeOnboarding';
// Recruitment Flow Pages
import JobPostings from './pages/recruitment/JobPostings';
import Candidates from './pages/recruitment/Candidates';
import Screening from './pages/recruitment/Screening';
import Interviews from './pages/recruitment/Interviews';
import AddEmployeeStreamlined from './pages/AddEmployeeStreamlined';
import HRSettings from './pages/HRSettings';
import Noticeboard from './pages/Noticeboard';
import HolidayCalendar from './pages/HolidayCalendar';
import DepartmentManagement from './pages/DepartmentManagement';
import GradeManagement from './pages/GradeManagement';
import BranchConfig from './pages/BranchConfig';
import AttendanceConfig from './pages/AttendanceConfig';
import TravelRatesConfig from './pages/TravelRatesConfig';
import CompanyInfo from './pages/CompanyInfo';
import DocumentGeneration from './pages/DocumentGeneration';
import UnifiedJoiningFlow from './pages/UnifiedJoiningFlow';
import ExitManagement from './pages/ExitManagement';
import EmailTemplates from './pages/EmailTemplates';
import Accounting from './pages/Accounting';
import LaptopIQC from './pages/LaptopIQC';
// Role-specific dashboards
import AdminDashboard from './pages/AdminDashboard';
import HRDashboard from './pages/HRDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import BrandMaster from './pages/BrandMaster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          {/* Shop (B2C Public Website) Routes - No Auth Required */}
          <Route path="/shop" element={<ShopHomepage />} />
          <Route path="/shop/about" element={<ShopAbout />} />
          <Route path="/shop/contact" element={<ShopContact />} />
          <Route path="/shop/stores" element={<ShopStores />} />
          <Route path="/shop/policy/:policyType" element={<ShopPolicy />} />
          <Route path="/shop/privacy" element={<Navigate to="/shop/policy/privacy" />} />
          <Route path="/shop/terms" element={<Navigate to="/shop/policy/terms" />} />
          <Route path="/shop/return-policy" element={<Navigate to="/shop/policy/return-policy" />} />
          <Route path="/shop/warranty-policy" element={<Navigate to="/shop/policy/warranty-policy" />} />
          <Route path="/shop/shipping-policy" element={<Navigate to="/shop/policy/shipping-policy" />} />
          <Route path="/shop/disclaimer" element={<Navigate to="/shop/policy/disclaimer" />} />
          <Route path="/shop/faqs" element={<Navigate to="/shop/policy/faqs" />} />
          
          {/* ERP Routes */}
          <Route 
            path="/login" 
            element={!user ? <Login setUser={setUser} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/vendor-registration" 
            element={<VendorRegistration />} 
          />
          <Route 
            path="/" 
            element={<RequireAuth user={user}><Dashboard user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          {/* Role-specific Dashboards */}
          <Route 
            path="/dashboard/admin" 
            element={<RequireAuth user={user}><AdminDashboard user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/dashboard/hr" 
            element={user ? <HRDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/dashboard/manager" 
            element={user ? <ManagerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/dashboard/employee" 
            element={user ? <EmployeeDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/employees" 
            element={user ? <EmployeeList user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/employees/new" 
            element={user ? <EmployeeFormComprehensive user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/onboarding/new" 
            element={user ? <AddEmployeeStreamlined user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/employees/:id" 
            element={user ? <EmployeeProfile user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/employees/:id/edit" 
            element={user ? <EmployeeFormComprehensive user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/color-palette" 
            element={<ColorPalette />} 
          />
          <Route 
            path="/departments" 
            element={user ? <Departments user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/location-master" 
            element={user ? <LocationMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/department-master" 
            element={user ? <DepartmentMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/designation-master" 
            element={user ? <DesignationMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leave-types-master" 
            element={user ? <LeaveTypesMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/change-password" 
            element={user ? <PasswordChange user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/attendance" 
            element={user ? <AttendanceWithCamera user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leaves" 
            element={user ? <Leaves user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leave-config" 
            element={user ? <LeaveConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leave-configuration" 
            element={user ? <LeaveConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leave-balances" 
            element={user ? <LeaveBalanceAllocation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leave-balance-allocation" 
            element={user ? <LeaveBalanceAllocation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/attendance-regularization" 
            element={user ? <AttendanceRegularization user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/shifts" 
            element={user ? <ShiftManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/holiday-management" 
            element={user ? <HolidayManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/offsite-attendance" 
            element={user ? <OffsiteAttendanceV2 user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/bulk-operations" 
            element={user ? <BulkOperationsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/file-management" 
            element={user ? <FileManagementPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/notifications" 
            element={user ? <NotificationsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/claims-management" 
            element={user ? <ClaimsManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/reports-analytics" 
            element={user ? <ReportsAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/onboarding-workflow" 
            element={user ? <OnboardingWorkflowPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/system-settings" 
            element={user ? <SystemSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/hr-settings" 
            element={<RequireAuth user={user}><HRSettings user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/noticeboard" 
            element={<RequireAuth user={user}><Noticeboard user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/holidays" 
            element={<RequireAuth user={user}><HolidayCalendar user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/departments-config" 
            element={<RequireAuth user={user}><DepartmentManagement user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/profiles-config" 
            element={<RequireAuth user={user}><ProfileManagement user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/grades-config" 
            element={<RequireAuth user={user}><GradeManagement user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/branch-config" 
            element={<RequireAuth user={user}><BranchConfig user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          <Route 
            path="/attendance-config" 
            element={user ? <AttendanceConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/travel-rates-config" 
            element={user ? <TravelRatesConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/company-info" 
            element={user ? <CompanyInfo user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/document-generation" 
            element={user ? <DocumentGeneration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/unified-joining-flow" 
            element={user ? <UnifiedJoiningFlow user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/exit-management" 
            element={user ? <ExitManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payroll" 
            element={user ? <Payroll user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payroll-configuration" 
            element={user ? <PayrollConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payslip-management" 
            element={user ? <PayslipManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payslips" 
            element={user ? <PayslipManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/compliance-reports" 
            element={user ? <ComplianceReports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/performance" 
            element={user ? <UnifiedPerformanceManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/approvals" 
            element={user ? <ApprovalDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/onboarding-dashboard" 
            element={user ? <OnboardingDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/employee-self-service" 
            element={user ? <EmployeeSelfService user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/recruitment" 
            element={user ? <RecruitmentDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/hrms" 
            element={user ? <EnhancedHRDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/hrms/employees" 
            element={user ? <EmployeeList user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/hrms/leaves" 
            element={user ? <Leaves user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/tasks" 
            element={user ? <KanbanBoard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/whatsapp-settings" 
            element={user ? <WhatsAppSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/whatsapp-widget" 
            element={user ? <WhatsAppWidget user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-inbox" 
            element={user ? <WhatsAppInbox user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-contacts" 
            element={user ? <WhatsAppContacts user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-campaigns" 
            element={user ? <WhatsAppCampaigns user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-analytics" 
            element={user ? <WhatsAppAnalyticsAdvanced user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-automation" 
            element={user ? <WhatsAppAutomation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-canned-responses" 
            element={user ? <WhatsAppCannedResponses user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/whatsapp-chatbot" 
            element={user ? <WhatsAppChatbotSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/email" 
            element={user ? <EmailInbox user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/email-management" 
            element={user ? <EmailManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/drive" 
            element={user ? <DocumentDrive user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/b2c-cms" 
            element={user ? <B2CCMS user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/email-templates" 
            element={user ? <EmailTemplates user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/accounting" 
            element={user ? <Accounting user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/documents" 
            element={user ? <Documents user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/announcements" 
            element={user ? <Announcements user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/onboarding" 
            element={user ? <Onboarding user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/shifts" 
            element={user ? <Shifts user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/holidays" 
            element={user ? <Holidays user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/company-policies" 
            element={user ? <CompanyPolicies user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/assets" 
            element={user ? <AllAssets user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/assets/dashboard" 
            element={user ? <AssetDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/assets/allocation" 
            element={user ? <AssetAllocation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/assets/returns" 
            element={user ? <AssetReturns user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/assets/history" 
            element={user ? <AssetHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/policies" 
            element={user ? <HRPolicies user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/expenses" 
            element={user ? <TravelExpense user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/legal" 
            element={user ? <Legal user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/crm" 
            element={user ? <CRM user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/customer-dashboard" 
            element={user ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/customer-360" 
            element={user ? <Customer360 user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/my-account" 
            element={user ? <MyAccount user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* Employee Self-Service Routes */}
          <Route 
            path="/travel-expense" 
            element={user ? <TravelExpense user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/my-leaves" 
            element={user ? <Leaves user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/apply-leave" 
            element={user ? <Leaves user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/profile" 
            element={user ? <EmployeeSelfService user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/my-tasks" 
            element={user ? <KanbanBoard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/my-performance" 
            element={user ? <UnifiedPerformanceManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/id-cards" 
            element={user ? <IDCardManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/bulk-upload" 
            element={user ? <BulkUploadPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/profiles" 
            element={user ? <ProfileManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/appointment-letters" 
            element={user ? <AppointmentLetterFlow user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/onboarding/new" 
            element={user ? <EnhancedEmployeeOnboarding user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/shifts" 
            element={user ? <ShiftManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/call-center" 
            element={user ? <CallCenter user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/express-ivr-call-center" 
            element={user ? <ExpressIVRCallCenter user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/roles" 
            element={user ? <RoleManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/reports" 
            element={user ? <Reports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/pricing-discussion" 
            element={<PricingDiscussion />} 
          />
          <Route 
            path="/products" 
            element={user ? <ProductManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/products/:id" 
            element={user ? <ProductDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/product-purchase" 
            element={user ? <ProductPurchase user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/brands" 
            element={user ? <BrandMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* QC Stage Configuration Routes */}
          <Route 
            path="/buyback/cqc-config" 
            element={user ? <CQCConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/sell" 
            element={<SellPhoneWidget />} 
          />
          <Route 
            path="/trc/iqc-queue" 
            element={user ? <IQCQueue user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/iqc-config" 
            element={user ? <IQCConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/franchisee" 
            element={user ? <FranchiseeManagementUnified user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/leads" 
            element={user ? <FranchiseeManagementUnified user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/franchisee-journey" 
            element={user ? <FranchiseeManagementUnified user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/store-operations" 
            element={user ? <StoreOperations user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/store-setup" 
            element={user ? <StoreSetup user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/supplier-management" 
            element={user ? <SupplierManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/purchase-management" 
            element={user ? <PurchaseManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/requisitions" 
            element={user ? <RequisitionManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/requisition-approval/:id" 
            element={user ? <RequisitionApproval user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/iqc-form" 
            element={user ? <IQCForm user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc" 
            element={user ? <TRCManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/parameter-mapping" 
            element={user ? <ParameterGradeMapping user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/approver-review/:iqcId" 
            element={user ? <ApproverReviewNew user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/approver-test" 
            element={user ? <ApproverTestScreen user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/bin-master" 
            element={user ? <BinMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/stn" 
            element={user ? <WarehouseSTN user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/srn-receive" 
            element={user ? <TRCSRN user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/engineer/dashboard" 
            element={user ? <EngineerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/inventory" 
            element={user ? <TRCInventory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/analytics" 
            element={user ? <TRCAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/device/:deviceId" 
            element={user ? <DeviceDetails user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/notifications" 
            element={user ? <NotificationsCenter user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/parts/inventory" 
            element={user ? <PartsInventory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/devices/bulk" 
            element={user ? <TRCDevicesBulk user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/status-dashboard" 
            element={user ? <StatusDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/asc/performance" 
            element={user ? <ASCPerformance user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/qc/dashboard" 
            element={user ? <QCDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/qc/iqc-engine" 
            element={user ? <IQCEngine user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/qc/fqc-engine" 
            element={user ? <FQCEngine user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* QC Management Routes */}
          <Route 
            path="/qc-control" 
            element={user ? <QCControlModule user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/control" 
            element={user ? <QCControlModule user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/cqc-config" 
            element={user ? <CQCConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/pqc-config" 
            element={user ? <PQCGradingConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/iqc-config" 
            element={user ? <IQCConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/fqc-config" 
            element={user ? <FQCEngine user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/iqc-queue" 
            element={user ? <IQCQueue user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/fqc-queue" 
            element={user ? <FQCPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/laptop-iqc" 
            element={user ? <LaptopIQC user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/qc/dashboard" 
            element={user ? <QCDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* CQC Customer Flow - Public Route */}
          <Route 
            path="/sell-phone" 
            element={<CQCFlow />} 
          />
          <Route 
            path="/cqc" 
            element={<CQCFlow />} 
          />
          <Route 
            path="/trc/binning" 
            element={user ? <TRCBinning user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/engineer/devices" 
            element={user ? <EngineerDevices user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/engineer/repair/:device_id" 
            element={user ? <RepairManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/fqc" 
            element={user ? <FQCPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/admin" 
            element={user ? <TRCAdmin user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/parts-consumption" 
            element={user ? <PartsConsumption user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/asc/manage" 
            element={user ? <ASCManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/asc/dispatch/:asc_id" 
            element={user ? <ASCDispatch user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/asc/dispatches" 
            element={user ? <ASCDispatches user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/supplier-portal" 
            element={<SupplierPortal />} 
          />
          <Route 
            path="/finance-management" 
            element={<RequireAuth user={user}><FinanceManagement user={user} onLogout={handleLogout} /></RequireAuth>} 
          />
          {/* New TRC Management Routes */}
          <Route 
            path="/trc/inventory/dashboard" 
            element={user ? <TRCInventoryDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/inventory/create-stn" 
            element={user ? <CreateSTN user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/inventory/create-srn" 
            element={user ? <CreateSRN user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/inventory/store" 
            element={user ? <StoreInventory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/inventory/rfr" 
            element={user ? <RFRManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/productivity" 
            element={user ? <ProductivityDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/spare/dashboard" 
            element={user ? <SpareDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/spare/master" 
            element={user ? <SpareMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/spare/bom" 
            element={user ? <BOMManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/spare/journey" 
            element={user ? <SpareJourney user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/spare/part-request" 
            element={user ? <PartRequest user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/repair/dashboard" 
            element={user ? <RepairDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/grading/config" 
            element={user ? <GradingEngineConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/asc/dashboard" 
            element={user ? <ASCDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/asc/create-dispatch" 
            element={user ? <CreateASCDispatch user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/dashboard" 
            element={user ? <WarehouseDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/manifests" 
            element={user ? <BuybackManifests user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/grn" 
            element={user ? <GRNProcessing user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/did-labels" 
            element={user ? <DIDLabelPrinting user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/bin-master" 
            element={user ? <BinMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/label-config" 
            element={user ? <LabelConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/masters/label-config" 
            element={user ? <LabelConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/create-grn" 
            element={user ? <CreateGRN user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/warehouse/reports" 
            element={user ? <WarehouseReports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/trc/laptop-iqc" 
            element={user ? <LaptopIQC user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/oqc-form" 
            element={user ? <OQCForm user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* <Route 
            path="/qc-severity-config" 
            element={user ? <QCSeverityConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          /> */}
          <Route 
            path="/qc-master-config" 
            element={user ? <QCMasterConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/pqc-grading-config" 
            element={user ? <PQCGradingConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* Recruitment Flow Routes */}
          <Route 
            path="/recruitment/jobs" 
            element={user ? <JobPostings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/recruitment/candidates" 
            element={user ? <Candidates user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/recruitment/screening" 
            element={user ? <Screening user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/recruitment/interviews" 
            element={user ? <Interviews user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          {/* Public Buyback Page - No Auth Required */}
          <Route path="/sell-phone" element={<SellPhone />} />
          
          {/* Pickup Engineer PWA - Separate Auth */}
          <Route path="/engineer" element={<EngineerApp />} />
          <Route path="/engineer/pickup/:leadId" element={<PickupDetail />} />
          <Route path="/engineer/performance" element={<EngineerPerformanceDashboard />} />
          
          {/* Admin Engineer Management */}
          <Route 
            path="/admin/engineer-leaderboard" 
            element={user ? <EngineerLeaderboard /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/engineer-performance/:engineerId" 
            element={user ? <EngineerPerformanceDashboard /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin/engineer-tracking" 
            element={user ? <EngineerLiveTracking user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          {/* Admin Buyback Management */}
          <Route 
            path="/buyback-dashboard" 
            element={user ? <BuybackDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/buyback-leads" 
            element={user ? <BuybackLeads user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/buyback-whatsapp" 
            element={user ? <BuybackWhatsAppTemplates user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/buyback-qc-config" 
            element={user ? <PQCConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/buyback-price-comparison" 
            element={user ? <BuybackPriceComparison user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
        </Routes>
      </BrowserRouter>
    </SidebarProvider>
  );
}

export default App;
